'use client';

import type { AccordionGlobalFilterItem, SearchListResponse } from '@weberus/shared-types';
import type { FC } from 'react';
import { Accordion, Chip, SubHeader } from '@yanolja-rnd/nol-design-system';
import { useMemo, useState } from 'react';
import { MoreButton } from './item/more-button';

/** (기본) 최대 5개 대지역 노출 */
const MAX_ITEM_COUNT = 5;

interface AccordionTypeFilterProps extends AccordionGlobalFilterItem {
  onChipClick: ({
    accordionIdx,
    chipIdx,
    isSelectAllItem,
  }: {
    accordionIdx: number;
    chipIdx: number;
    isSelectAllItem: boolean;
  }) => void;
  onMoreButtonClick: (logMeta: SearchListResponse['serverLogMeta']) => void;
}

export const AccordionTypeFilter: FC<AccordionTypeFilterProps> = ({
  title,
  items,
  serverLogMeta,
  onChipClick,
  onMoreButtonClick,
}) => {
  const [isFold, setIsFold] = useState(true); // 더보기 버튼 클릭 시, 접기/펼치기 상태 변경
  const [openedAccordionIdx, setOpenedAccordionIdx] = useState<number | null>(0); // 1번째 대지역은 펼쳐져 있는 상태로 제공
  const hasSelectedItems = items.some((item) => item.items.some((chip) => chip.selected));

  const renderItems = useMemo(() => (isFold ? items.slice(0, MAX_ITEM_COUNT) : items), [isFold, items]);
  const hasMoreItems = items.length > MAX_ITEM_COUNT;

  // 노출하는 대지역이 1개인 경우, 접기/펼치기 미노출
  const isItemOnlyOne = items.length === 1;

  const handleTitleClick = (idx: number): void => {
    if (isItemOnlyOne) return;
    setOpenedAccordionIdx(openedAccordionIdx === idx ? null : idx);
  };

  return (
    <section className="px-16 pb-32 pt-16">
      <SubHeader.Small title={title} showDotBadge={hasSelectedItems} />
      {/* todo isItemOnlyOne 일때는 아코디언이 펼쳐지면 안됨 */}
      <Accordion
        items={renderItems.map((accordion, accordionIdx) => ({
          title: accordion.title,
          initialOpen: openedAccordionIdx === accordionIdx,
          content: (
            <div className="flex flex-wrap gap-8 pb-16 pt-8">
              {accordion.items.map((item, idx) => (
                <Chip
                  key={idx}
                  selected={item.selected}
                  size="medium"
                  theme="outlined"
                  onClick={() => {
                    onChipClick({
                      accordionIdx,
                      chipIdx: idx,
                      isSelectAllItem: !!item.isSelectAllItem,
                    });
                  }}>
                  {
                    <div className="relative inline-block">
                      {/* 투명 텍스트로 공간 확보 */}
                      <span className="invisible font-bold" aria-hidden="true">
                        {item.title}
                      </span>
                      {/* 실제 렌더링될 텍스트 */}
                      <span className="absolute left-0 whitespace-nowrap">{item.title}</span>
                    </div>
                  }
                </Chip>
              ))}
            </div>
          ),
        }))}
        onClick={(_e, index) => {
          handleTitleClick(index);
        }}
      />

      {/* 대지역이 5개를 초과할 경우 하단 더보기 버튼 노출 */}
      {!!hasMoreItems && (
        <MoreButton
          isFold={isFold}
          setIsFold={setIsFold}
          onClick={() => {
            onMoreButtonClick(serverLogMeta);
          }}
        />
      )}
    </section>
  );
};
